import { useEffect, useRef, useState } from "react";
import "./AgroFlyte.css";

const STATS = [
    { value: 4, suffix: "", label: "Veteran Pilots", sub: "Behind every physics detail" },
    { value: 500, suffix: "+", label: "Flight Hours", sub: "Poured into accuracy" },
    { value: 3, suffix: "", label: "Drone Models", sub: "Spreader & Sprayer variants" },
    { value: 7, suffix: "", label: "Crop Varieties", sub: "Across 3 growth stages" },
];

const PILLARS = [
    {
        num: "01",
        title: ["Born From", "Real Skies"],
        body: "AgroFlyte wasn't designed in a boardroom. It was shaped over hundreds of hours of actual field flying — four experienced agri drone pilots brought every nuance of real flight into this simulation. The way a drone responds to throttle, the drift during a turn, the rhythm of a spray pass — all of it is here because someone lived it first.",
        tag: "Origin",
    },
    {
        num: "02",
        title: ["Physics", "That Don't Lie"],
        body: "Built on Unreal Engine 5 with a fully physics-driven flight model, AgroFlyte doesn't simplify how a drone flies — it replicates it. Every control input, every gust response, every hover correction behaves exactly the way it does in the field. That's thousands of decisions made by people who know what a real agri drone feels like.",
        tag: "Technology",
    },
    {
        num: "03",
        title: ["The Same", "Transmitter"],
        body: "Your trainees control AgroFlyte using the Taranis QX7 — the exact same transmitter used in real agri drone operations. There's no transition shock when they step into the field. The muscle memory they build here is the muscle memory they carry out there. That's intentional, and that's what makes this genuinely useful.",
        tag: "Hardware",
    },
    {
        num: "04",
        title: ["Crops That", "Grow With You"],
        body: "Seven crop varieties. Three growth stages — seeding, pre-harvest, and harvest. Each stage changes what your trainee sees and how they need to operate. Because real agri drone work isn't one-size-fits-all, and neither should your training be. AgroFlyte gives your pilots the range they need to be ready for whatever the field demands.",
        tag: "Simulation",
    },
];

function useCountUp(target, duration = 1800, active = false) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!active) return;
        let startTime = null;
        const step = (ts) => {
            if (!startTime) startTime = ts;
            const p = Math.min((ts - startTime) / duration, 1);
            const ease = 1 - Math.pow(1 - p, 4);
            setCount(Math.floor(ease * target));
            if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [active, target, duration]);
    return count;
}

function StatItem({ value, suffix, label, sub, delay, active }) {
    const count = useCountUp(value, 1800, active);
    return (
        <div className="af2-stat" style={{ "--d": delay }}>
            <div className="af2-stat__figure">
                <span className="af2-stat__val">{active ? count : 0}</span>
                <span className="af2-stat__sfx">{suffix}</span>
            </div>
            <div className="af2-stat__divider" />
            <p className="af2-stat__label">{label}</p>
            <p className="af2-stat__sub">{sub}</p>
        </div>
    );
}

export default function AgroFlyteAbout() {
    const statsRef = useRef(null);
    const headerRef = useRef(null);
    const quoteRef = useRef(null);
    const pillarsRef = useRef([]);

    const [statsOn, setStatsOn] = useState(false);
    const [headerOn, setHeaderOn] = useState(false);
    const [quoteOn, setQuoteOn] = useState(false);
    const [pillarsOn, setPillarsOn] = useState([false, false, false, false]);

    useEffect(() => {
        const io = new IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting }) => {
                if (!isIntersecting) return;
                if (target === statsRef.current) setStatsOn(true);
                if (target === headerRef.current) setHeaderOn(true);
                if (target === quoteRef.current) setQuoteOn(true);
                pillarsRef.current.forEach((el, i) => {
                    if (target === el)
                        setPillarsOn(prev => { const n = [...prev]; n[i] = true; return n; });
                });
            });
        }, { threshold: 0.12 });

        [statsRef, headerRef, quoteRef].forEach(r => r.current && io.observe(r.current));
        pillarsRef.current.forEach(el => el && io.observe(el));
        return () => io.disconnect();
    }, []);

    return (
        <section className="af2" id="about">

            {/* ── Ambient bg ── */}
            <div className="af2__grid" aria-hidden="true" />
            <div className="af2__vignette" aria-hidden="true" />
            <div className="af2__orb af2__orb--a" aria-hidden="true" />
            <div className="af2__orb af2__orb--b" aria-hidden="true" />
            <div className="af2__orb af2__orb--c" aria-hidden="true" />

            <div className="af2__wrap">

                {/* ═══ HEADER ═══ */}
                <header className={`af2__header ${headerOn ? "af2__in" : ""}`} ref={headerRef}>

                    <div className="af2__eyebrow">
                        <span className="af2__eyebrow-dot" />
                        <span className="af2__eyebrow-line" />
                        <span className="af2__eyebrow-text">About AgroFlyte</span>
                        <span className="af2__eyebrow-line" />
                    </div>

                    <h2 className="af2__h2">
                        <span className="af2__h2-row">The closest your trainee</span>
                        <span className="af2__h2-row af2__h2-row--gold">will get to a real drone —</span>
                        <span className="af2__h2-row af2__h2-row--italic">without leaving the ground.</span>
                    </h2>

                    <p className="af2__lead">
                        AgroFlyte is a professional agri drone flight simulator built from the ground up by those who've spent their careers in the sky. It exists for one reason — to give the next generation of drone pilots a safe, accurate, and truly effective place to learn before they fly for real.
                    </p>

                </header>

                {/* ═══ STATS ═══ */}
                <div className="af2__stats-shell" ref={statsRef}>
                    <div className="af2__stats">
                        {STATS.map((s, i) => (
                            <StatItem key={s.label} {...s} delay={`${i * 0.1}s`} active={statsOn} />
                        ))}
                    </div>
                    <div className="af2__stats-caption">
                        <span className="af2__stats-cap-line" />
                        <span>Grounded in real experience</span>
                        <span className="af2__stats-cap-line" />
                    </div>
                </div>

                {/* ═══ QUOTE ═══ */}
                <figure className={`af2__quote ${quoteOn ? "af2__in" : ""}`} ref={quoteRef}>
                    <div className="af2__quote-bg" aria-hidden="true" />
                    <span className="af2__quote-mark" aria-hidden="true">"</span>
                    <blockquote className="af2__quote-text">
                        This isn't a game with drone controls. This is a simulator built by pilots, for pilots — where every detail exists because real flight demanded it.
                    </blockquote>
                    <figcaption className="af2__quote-cap">
                        <span className="af2__quote-rule" />
                        <span className="af2__quote-brand">Aviatricks Aero Labs</span>
                        <span className="af2__quote-sep">·</span>
                        <span className="af2__quote-tagline">Precision. Practice. Progress.</span>
                    </figcaption>
                </figure>

                {/* ═══ PILLARS ═══ */}
                <div className="af2__pillars">
                    {PILLARS.map((p, i) => (
                        <article
                            key={p.num}
                            className={`af2__pillar ${pillarsOn[i] ? "af2__in" : ""}`}
                            ref={el => (pillarsRef.current[i] = el)}
                            style={{ "--d": `${i * 0.13}s` }}
                        >
                            <div className="af2__pillar-header">
                                <span className="af2__pillar-num">{p.num}</span>
                                <span className="af2__pillar-tag">{p.tag}</span>
                            </div>

                            <h3 className="af2__pillar-title">
                                {p.title.map((line, j) => (
                                    <span key={j}>{line}</span>
                                ))}
                            </h3>

                            <div className="af2__pillar-rule" />
                            <p className="af2__pillar-body">{p.body}</p>

                            {/* Decorative corners */}
                            <span className="af2__pillar-c af2__pillar-c--tl" aria-hidden="true" />
                            <span className="af2__pillar-c af2__pillar-c--br" aria-hidden="true" />
                            {/* Hover glow */}
                            <span className="af2__pillar-glow" aria-hidden="true" />
                        </article>
                    ))}
                </div>

            </div>
        </section>
    );
}